import { assert, log, error } from '../app/logger.js';
import { services } from '../app/services.js';
import { Location } from './location.js';

/**
 * Locations.
 */
class Locations {
  get(id) {
    assert(
      typeof id == 'number',
      `[data] Locations.get wrong location id given, value: ${id}, type: ${typeof id}`
    );
    return this.#hash.get(id) || this.#cache({ id });
  }

  from(locData) {
    return this.#hash.get(locData.id) || this.#cache(locData);
  }

  async getInArea({ north, east, south, west }) {
    if (!north || !east || !south || !west) {
      error(
        `Failed to retrieve maps bounds: north: ${north}, east: ${east}, south: ${south}, west: ${west}`
      );
      return null;
    }

    let locDataList = await services.location.getLocations({
      north,
      east,
      south,
      west,
    });
    return locDataList.map(locData => this.#cache(locData));
  }

  async byIds(ids) {
    let locDataList = await services.location.getLocationsByIds(ids);
    return locDataList.map(locData => this.#cache(locData));
  }

  byGoogleId(google_id) {
    return this.#googleIdHash.get(google_id);
  }

  asList() {
    return this.#hash.values();
  }

  // Adds a new location.
  async add(locData) {
    log(`[data] add location for: ${JSON.stringify(locData)}`);

    let id = await services.location.addLocation(locData);
    if (id == 0) {
      error(`[data] failed to add location for ${JSON.stringify(locData)}`);
      return null;
    }

    // https://github.com/eslint/eslint/issues/11899
    locData.id = id; // eslint-disable-line require-atomic-updates
    locData.readonly = false; // eslint-disable-line require-atomic-updates
    locData.moderation_wanted = false; // eslint-disable-line require-atomic-updates
    return this.#cache(locData);
  }

  // Changes the existing location.
  async change(locData) {
    // Update local cache if coordinates were changed.
    let loc = this.#hash.get(locData.id);
    if (!loc) {
      error(`[data] no '${locData.name}':${locData.id} location to change`);
      return;
    }
    loc.merge(locData);
    await services.location.changeLocation(locData);
    return loc;
  }

  remove(loc) {
    if (loc.google_id) {
      this.#googleIdHash.delete(loc.google_id);
    }
    this.#hash.delete(loc.id);
    return services.location.removeLocation(loc.id);
  }

  async search(options) {
    let locs = await services.location.searchLocations(options);
    return locs.map(loc => this.#cache(loc));
  }

  setDataProvider(data) {
    return services.location.setDataProvider(data);
  }

  // Create Location instance and cache it.
  #cache(locData) {
    // Update existing location if any.
    let loc = this.#hash.get(locData.id);
    if (loc) {
      loc.merge(locData);
      return loc;
    }

    // Merge with existing google location if any.
    if (locData.id) {
      loc = this.#googleIdHash.get(locData.google_id);
      if (loc) {
        loc.merge(locData);
        this.#hash.set(locData.id, loc);
        return loc;
      }
    }

    // Create new location.
    loc = new Location(locData, getLocationFrom);
    if (locData.id) {
      this.#hash.set(locData.id, loc);
    }
    if (locData.google_id) {
      this.#googleIdHash.set(locData.google_id, loc);
    }
    return loc;
  }

  #googleIdHash = new Map();
  #hash = new Map();
}

const locations = new Locations();
const getLocationFrom = locData => locations.from(locData);

export { locations };
